import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  EventNote as EventNoteIcon,
  CalendarToday as CalendarTodayIcon,
  Edit as EditIcon,
  SupervisorAccount as SupervisorAccountIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../axios';
import ContextNavBar from '../components/ContextNavBar';

function AreaProfessor({ onLogout }) {
  const [professor, setProfessor] = useState(null);
  const [turmas, setTurmas] = useState([]);
  const [aulas, setAulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    nome: '',
    telefone_whatsapp: '',
    endereco: '',
    bairro: '',
    cep: '',
    data_nascimento: '',
    e_monitor: false
  });
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    fetchProfessorData();
  }, []);

  const fetchProfessorData = async () => {
    try {
      // Buscar dados do professor logado
      const professorResponse = await api.get('/professor/perfil');
      
      if (professorResponse.status === 401) {
        // Token expirado ou inválido - fazer logout
        localStorage.removeItem('token');
        onLogout();
        return;
      }
      
      const professorData = professorResponse.data;
      setProfessor(professorData);

      // Se não houver id (ex: admin sem registro de Professor), não chamar endpoints que exigem id
      if (professorData && professorData.id) {
        // Buscar turmas do professor
        const turmasResponse = await api.get(`/professor/${professorData.id}/turmas`);
        setTurmas(turmasResponse.data);

        // Buscar aulas do professor
        const aulasResponse = await api.get(`/professor/${professorData.id}/aulas`);
        setAulas(aulasResponse.data);
      } else {
        setTurmas([]);
        setAulas([]);
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('token');
        onLogout();
      } else {
        setError('Erro ao conectar com o servidor');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = () => {
    if (professor) {
      setEditFormData({
        nome: professor.nome || '',
        telefone_whatsapp: professor.telefone || '',
        endereco: professor.endereco || '',
        bairro: professor.bairro || '',
        cep: professor.cep || '',
        data_nascimento: professor.data_nascimento || '',
        e_monitor: professor.e_monitor || false
      });
      setEditDialogOpen(true);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSaveSuccess(false);
  };

  const handleEditFormChange = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveProfile = async () => {
    try {
      const response = await api.put('/professor/perfil', editFormData);
      // Atualizar estado local
      setProfessor(response.data.professor);
      // Forçar recarregar perfil completo para garantir sincronização
      const perfilResponse = await api.get('/professor/perfil');
      setProfessor(perfilResponse.data);
      setSaveSuccess(true);
      setTimeout(() => {
        handleCloseEditDialog();
      }, 1500);
    } catch {
      setError('Erro ao atualizar dados');
    }
  };  const formatData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <Box>
        <Header onLogout={onLogout} />
        <ContextNavBar context="startfy" />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Typography>Carregando...</Typography>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box>
      <Header onLogout={onLogout} />
      <ContextNavBar context="startfy" />
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Cabeçalho com informações do professor */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h4" sx={{ fontSize: { xs: '1.3rem', md: '2rem' } }}>
                  Área do Professor
                </Typography>
                <IconButton
                  color="primary"
                  onClick={handleOpenEditDialog}
                  title="Editar meus dados"
                  size="small"
                >
                  <EditIcon />
                </IconButton>
              </Box>
              {professor && (
                <Box>
                  <Typography variant="h6" color="primary">
                    {professor.nome}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {professor.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Telefone: {professor.telefone || 'Não informado'}
                  </Typography>
                  {professor.data_nascimento && (
                    <Typography variant="body2" color="text.secondary">
                      Data de Nascimento: {new Date(professor.data_nascimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </Typography>
                  )}
                  {professor.endereco && (
                    <Typography variant="body2" color="text.secondary">
                      Endereço: {professor.endereco}
                      {professor.bairro && `, ${professor.bairro}`}
                      {professor.cep && ` - CEP: ${professor.cep}`}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CalendarTodayIcon />}
              component={RouterLink}
              to="/calendario-aulas"
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              Calendário de Aulas
            </Button>
          </Box>
        </Paper>

        {/* Cards de resumo */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <SchoolIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" component="div">
                  {turmas.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Turmas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" component="div">
                  {professor?.total_aulas_professor || aulas.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Aulas como Professor
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {professor?.e_monitor && (
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <SupervisorAccountIcon sx={{ fontSize: 40, color: '#2196f3', mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {professor?.total_aulas_monitor || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aulas como Monitor
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" component="div">
                  {turmas.reduce((total, turma) => total + (turma.total_alunos || 0), 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Alunos Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <EventNoteIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="h4" component="div">
                  {(professor?.proximas_aulas_professor || 0) + (professor?.proximas_aulas_monitor || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Próximas Aulas (Total)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Lista de Turmas */}
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
              Minhas Turmas
            </Typography>
            {turmas.length > 0 ? (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Nome da Turma</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Período</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Total de Alunos</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {turmas.map((turma) => (
                      <TableRow key={turma.id}>
                        <TableCell>{turma.nome}</TableCell>
                        <TableCell>{turma.periodo_nome}</TableCell>
                        <TableCell>{turma.total_alunos || 0}</TableCell>
                        <TableCell>
                          <Chip 
                            label="Ativa" 
                            color="success" 
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                Nenhuma turma atribuída
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Lista de Aulas */}
        <Paper>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
              Próximas Aulas
            </Typography>
            {aulas.length > 0 ? (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Data</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Turma</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Lição</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aulas.slice(0, 10).map((aula) => (
                      <TableRow key={aula.id}>
                        <TableCell>{formatData(aula.data)}</TableCell>
                        <TableCell>{aula.turma_nome}</TableCell>
                        <TableCell>{aula.licao || 'Não definida'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={new Date(aula.data) >= new Date().setHours(0,0,0,0) ? "Agendada" : "Realizada"} 
                            color={new Date(aula.data) >= new Date().setHours(0,0,0,0) ? "primary" : "default"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                Nenhuma aula agendada
              </Typography>
            )}
          </Box>
        </Paper>
      </Container>
      <Footer />

      {/* Dialog de Edição de Dados */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Meus Dados</DialogTitle>
        <DialogContent>
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Dados atualizados com sucesso!
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nome"
              name="nome"
              value={editFormData.nome}
              onChange={handleEditFormChange}
              fullWidth
              required
            />
            <TextField
              label="Telefone/WhatsApp"
              name="telefone_whatsapp"
              value={editFormData.telefone_whatsapp}
              onChange={handleEditFormChange}
              fullWidth
              placeholder="(00) 00000-0000"
            />
            <TextField
              label="Data de Nascimento"
              name="data_nascimento"
              type="date"
              value={editFormData.data_nascimento}
              onChange={handleEditFormChange}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="Endereço"
              name="endereco"
              value={editFormData.endereco}
              onChange={handleEditFormChange}
              fullWidth
              placeholder="Rua, número, complemento"
            />
            <TextField
              label="Bairro"
              name="bairro"
              value={editFormData.bairro}
              onChange={handleEditFormChange}
              fullWidth
            />
            <TextField
              label="CEP"
              name="cep"
              value={editFormData.cep}
              onChange={handleEditFormChange}
              fullWidth
              placeholder="00000-000"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.e_monitor}
                  onChange={(e) => setEditFormData({ ...editFormData, e_monitor: e.target.checked })}
                  color="primary"
                />
              }
              label="É Monitor?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={saveSuccess}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveProfile} 
            variant="contained" 
            color="primary"
            disabled={saveSuccess || !editFormData.nome}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AreaProfessor;